// src/features/privateAlbum/components/ShareModal.jsx

import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Chip,
  IconButton,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Avatar,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { ROLE_OPTIONS, ACCESS_STATUS } from "../types/privateAlbumTypes";
import { formatDateTime } from "../utils/privateAlbumHelpers";

const ShareModal = ({
  albumId,
  accessList,
  onShare,
  onRevoke,
  onCancel,
  loading,
}) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("viewer");
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccessMessage("");

    const newErrors = {};
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Invalid email format";
    }
    if (!name.trim()) {
      newErrors.name = "Name is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await onShare(email, name, role);
      setSuccessMessage(`${email} has been shared successfully!`);
      setEmail("");
      setName("");
      setRole("viewer");
    } catch (error) {
      setErrors({ submit: error.message });
    }
  };

  const handleRevoke = async (accessId) => {
    if (window.confirm("Are you sure you want to revoke access?")) {
      await onRevoke(accessId);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      [ACCESS_STATUS.PENDING]: "warning",
      [ACCESS_STATUS.ACCEPTED]: "success",
      [ACCESS_STATUS.REVOKED]: "error",
      [ACCESS_STATUS.EXPIRED]: "default",
    };
    return colors[status] || "default";
  };

  return (
    <Box>
      {/* Share Form */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Email *"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              placeholder="client@email.com"
              fullWidth
              disabled={loading}
            />

            <TextField
              label="Name *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              placeholder="Client Name"
              fullWidth
              disabled={loading}
            />

            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                label="Role"
                disabled={loading}
              >
                {ROLE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {errors.submit && <Alert severity="error">{errors.submit}</Alert>}
            {successMessage && (
              <Alert severity="success">{successMessage}</Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <PersonAddIcon />
                )
              }
            >
              {loading ? "Sharing..." : "Share Album"}
            </Button>
          </Stack>
        </form>
      </Paper>

      {/* Access List */}
      {accessList && accessList.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Shared With ({accessList.length})
          </Typography>
          <Paper variant="outlined" sx={{ maxHeight: 250, overflow: "auto" }}>
            <List dense>
              {accessList.map((access, index) => (
                <React.Fragment key={access.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              bgcolor: "primary.main",
                            }}
                          >
                            <PersonIcon sx={{ fontSize: 16 }} />
                          </Avatar>
                          <Typography variant="body2" fontWeight="medium">
                            {access.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {access.email}
                          </Typography>
                        </Stack>
                      }
                      secondary={
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ mt: 0.5 }}
                        >
                          <Chip
                            label={access.role}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={access.status}
                            size="small"
                            color={getStatusColor(access.status)}
                          />
                          {access.lastAccessed && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Last: {formatDateTime(access.lastAccessed)}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {access.accessCount || 0} views
                          </Typography>
                        </Stack>
                      }
                    />
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleRevoke(access.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>
                  {index < accessList.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>
      )}

      {/* Cancel Button */}
      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" color="inherit" fullWidth onClick={onCancel}>
          Close
        </Button>
      </Box>
    </Box>
  );
};

export default ShareModal;
